/**
 * Streaming Limits Service
 * Handles API calls for streaming time limits
 */

import { apiService } from './api';

export interface StreamingLimits {
  isUnlimited: boolean;
  remainingTime: number | null;
  dailyLimit: number | null;
  timeUsed: number;
  resetTime: string | null;
  isLimitExceeded?: boolean;
}

class StreamingLimitsService {
  /**
   * Fetch streaming limits from backend
   */
  async fetchLimits(): Promise<StreamingLimits> {
    try {
      const response = await apiService.get('/subscriptions/streaming/limits');
      
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      
      throw new Error('Invalid response format');
    } catch (error: any) {
      console.error('[StreamingLimits] Failed to fetch limits:', error);
      
      // If user is not authenticated, return default free tier limits
      if (error?.response?.status === 401 || error?.message?.includes('401')) {
        console.log('[StreamingLimits] User not authenticated, using default free tier limits');
        return {
          isUnlimited: false,
          remainingTime: 14400, // 4 hours default
          dailyLimit: 14400,
          timeUsed: 0,
          resetTime: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
        };
      }
      
      throw error;
    }
  }

  /**
   * Update streaming usage with backend
   */
  async updateUsage(durationSeconds: number): Promise<StreamingLimits> {
    try {
      const response = await apiService.post('/subscriptions/streaming/update-usage', {
        durationSeconds
      });
      
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      
      throw new Error('Invalid response format');
    } catch (error) {
      console.error('[StreamingLimits] Failed to update usage:', error);
      throw error;
    }
  }
}

export const streamingLimitsService = new StreamingLimitsService();
export default streamingLimitsService;
